/**
 * Polish VAT Registry API (Biała lista VAT) Integration
 *
 * API Documentation: https://www.gov.pl/web/kas/api-wykazu-podatnikow-vat
 * Base URL: https://wl-api.mf.gov.pl
 *
 * This service fetches company data from the Polish Ministry of Finance
 * VAT taxpayer registry (Wykaz podatników VAT / Biała lista VAT).
 */

const VAT_API_BASE_URL = 'https://wl-api.mf.gov.pl';

/**
 * VAT API response structure
 */
export interface VatApiResponse {
  result: {
    subject: VatSubject | null;
    requestId: string;
    requestDateTime: string;
  };
}

export interface VatSubject {
  name: string;
  nip: string;
  statusVat: string; // 'Czynny', 'Zwolniony', 'Niezarejestrowany'
  regon: string | null;
  pesel: string | null;
  krs: string | null;
  residenceAddress: string | null;
  workingAddress: string | null;
  representatives: VatRepresentative[] | null;
  authorizedClerks: VatAuthorizedClerk[] | null;
  partners: VatPartner[] | null;
  registrationLegalDate: string | null;
  registrationDenialBasis: string | null;
  registrationDenialDate: string | null;
  restorationBasis: string | null;
  restorationDate: string | null;
  removalBasis: string | null;
  removalDate: string | null;
  accountNumbers: string[] | null;
  hasVirtualAccounts: boolean;
}

export interface VatRepresentative {
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  nip: string | null;
  pesel: string | null;
}

export interface VatAuthorizedClerk {
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  nip: string | null;
  pesel: string | null;
}

export interface VatPartner {
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  nip: string | null;
  pesel: string | null;
}

/**
 * Parsed company data extracted from VAT API response
 */
export interface CompanyData {
  nip: string;
  companyName: string;
  address: string;
  city: string;
  postalCode: string;
  county: string; // powiat
  voivodeship: string; // województwo
  regon: string | null;
  krs: string | null;
  vatStatus: 'active' | 'exempt' | 'not_registered';
  vatRegistrationDate: string | null;
  vatRemovalDate: string | null;
  vatRestorationDate: string | null;
  bankAccounts: string[];
  rawResponse: VatApiResponse;
}

/**
 * Map Polish voivodeship names to slug format used in the app
 */
const VOIVODESHIP_MAP: Record<string, string> = {
  dolnośląskie: 'dolnoslaskie',
  'kujawsko-pomorskie': 'kujawsko-pomorskie',
  lubelskie: 'lubelskie',
  lubuskie: 'lubuskie',
  łódzkie: 'lodzkie',
  małopolskie: 'malopolskie',
  mazowieckie: 'mazowieckie',
  opolskie: 'opolskie',
  podkarpackie: 'podkarpackie',
  podlaskie: 'podlaskie',
  pomorskie: 'pomorskie',
  śląskie: 'slaskie',
  świętokrzyskie: 'swietokrzyskie',
  'warmińsko-mazurskie': 'warminsko-mazurskie',
  wielkopolskie: 'wielkopolskie',
  zachodniopomorskie: 'zachodniopomorskie',
};

/**
 * Validate NIP format (Polish tax identification number)
 * NIP consists of 10 digits with a checksum
 */
export function validateNip(nip: string): boolean {
  // Remove any non-digit characters
  const cleanNip = nip.replace(/\D/g, '');

  if (cleanNip.length !== 10) {
    return false;
  }

  // NIP checksum validation
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanNip[i], 10) * weights[i];
  }

  const checksum = sum % 11;

  // Checksum cannot be 10, so NIP is invalid in that case
  if (checksum === 10) {
    return false;
  }

  return checksum === parseInt(cleanNip[9], 10);
}

/**
 * Format NIP for display (XXX-XXX-XX-XX or XXX-XX-XX-XXX)
 */
export function formatNip(nip: string): string {
  const cleanNip = nip.replace(/\D/g, '');
  if (cleanNip.length !== 10) {
    return nip;
  }
  return `${cleanNip.slice(0, 3)}-${cleanNip.slice(3, 6)}-${cleanNip.slice(6, 8)}-${cleanNip.slice(8, 10)}`;
}

/**
 * Parse address string from VAT API into components
 * Address format examples:
 * - "ul. Przykładowa 123, 00-001 Warszawa"
 * - "ul. Testowa 45/6, 30-001 Kraków, pow. krakowski, woj. małopolskie"
 */
function parseAddress(address: string | null): {
  streetAddress: string;
  postalCode: string;
  city: string;
  county: string;
  voivodeship: string;
} {
  if (!address) {
    return {
      streetAddress: '',
      postalCode: '',
      city: '',
      county: '',
      voivodeship: '',
    };
  }

  let streetAddress = '';
  let postalCode = '';
  let city = '';
  let county = '';
  let voivodeship = '';

  // Extract voivodeship
  const voivodeshipMatch = address.match(/woj\.\s*(\S+)/i);
  if (voivodeshipMatch) {
    const rawVoivodeship = voivodeshipMatch[1].toLowerCase().replace(/,/g, '');
    voivodeship = VOIVODESHIP_MAP[rawVoivodeship] || rawVoivodeship;
  }

  // Extract county (powiat)
  const countyMatch = address.match(/pow\.\s*([^,]+)/i);
  if (countyMatch) {
    county = countyMatch[1].trim();
  }

  // Extract postal code and city
  const postalCityMatch = address.match(/(\d{2}-\d{3})\s+([^,]+)/);
  if (postalCityMatch) {
    postalCode = postalCityMatch[1];
    city = postalCityMatch[2].trim();
  }

  // Extract street address (everything before postal code)
  const postalIndex = address.indexOf(postalCode);
  if (postalIndex > 0) {
    streetAddress = address.substring(0, postalIndex).replace(/,\s*$/, '').trim();
  } else {
    // If no postal code found, try to extract street
    const streetMatch = address.match(/^([^,]+)/);
    if (streetMatch) {
      streetAddress = streetMatch[1].trim();
    }
  }

  return {
    streetAddress,
    postalCode,
    city,
    county,
    voivodeship,
  };
}

/**
 * Map VAT status from API to app format
 */
function mapVatStatus(status: string): 'active' | 'exempt' | 'not_registered' {
  const statusLower = status.toLowerCase();
  if (statusLower === 'czynny') {
    return 'active';
  }
  if (statusLower === 'zwolniony') {
    return 'exempt';
  }
  return 'not_registered';
}

/**
 * Get current date in YYYY-MM-DD format
 */
function getCurrentDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Fetch company data from VAT Registry API by NIP
 */
export async function fetchCompanyByNip(nip: string): Promise<CompanyData> {
  // Clean and validate NIP
  const cleanNip = nip.replace(/\D/g, '');

  if (!validateNip(cleanNip)) {
    throw new Error('Nieprawidłowy format NIP');
  }

  const date = getCurrentDate();
  const url = `${VAT_API_BASE_URL}/api/search/nip/${cleanNip}?date=${date}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error('Nieprawidłowy format NIP');
      }
      if (response.status === 404) {
        throw new Error('Nie znaleziono podmiotu o podanym NIP');
      }
      throw new Error(`Błąd API: ${response.status}`);
    }

    const data: VatApiResponse = await response.json();

    if (!data.result.subject) {
      throw new Error('Nie znaleziono podmiotu o podanym NIP');
    }

    const subject = data.result.subject;

    // Parse working address first, fallback to residence address
    const addressToParse = subject.workingAddress || subject.residenceAddress;
    const parsedAddress = parseAddress(addressToParse);

    return {
      nip: subject.nip,
      companyName: subject.name,
      address: parsedAddress.streetAddress,
      city: parsedAddress.city,
      postalCode: parsedAddress.postalCode,
      county: parsedAddress.county,
      voivodeship: parsedAddress.voivodeship,
      regon: subject.regon,
      krs: subject.krs,
      vatStatus: mapVatStatus(subject.statusVat),
      vatRegistrationDate: subject.registrationLegalDate,
      vatRemovalDate: subject.removalDate,
      vatRestorationDate: subject.restorationDate,
      bankAccounts: subject.accountNumbers || [],
      rawResponse: data,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Błąd połączenia z API VAT');
  }
}

/**
 * Check if a NIP is registered in VAT registry (quick check)
 */
export async function checkVatStatus(
  nip: string
): Promise<{ isRegistered: boolean; status: string | null }> {
  try {
    const data = await fetchCompanyByNip(nip);
    return {
      isRegistered: data.vatStatus === 'active',
      status: data.vatStatus,
    };
  } catch {
    return {
      isRegistered: false,
      status: null,
    };
  }
}

/**
 * Get VAT status display text in Polish
 */
export function getVatStatusText(status: 'active' | 'exempt' | 'not_registered'): string {
  switch (status) {
    case 'active':
      return 'Czynny podatnik VAT';
    case 'exempt':
      return 'Zwolniony z VAT';
    case 'not_registered':
      return 'Niezarejestrowany';
    default:
      return 'Nieznany';
  }
}

/**
 * Get VAT status badge color for UI
 */
export function getVatStatusColor(
  status: 'active' | 'exempt' | 'not_registered'
): 'green' | 'yellow' | 'red' {
  switch (status) {
    case 'active':
      return 'green';
    case 'exempt':
      return 'yellow';
    case 'not_registered':
      return 'red';
    default:
      return 'red';
  }
}
